/**
 * AGRI-OPERATIONAL-SECTOR-R2 — the single operational model of a sector.
 *
 * The Dashboard mini-cards and the Sector Detail page MUST agree on what a
 * sector is doing right now. Before R2 each surface re-derived its own view,
 * which allowed two different interpretations of the same telemetry. This
 * module is now the ONLY interpreter: both surfaces consume `buildSectorModel`.
 *
 * Truthfulness rules (non-negotiable):
 *   - A sector's data comes EXCLUSIVELY from nodes whose canonical
 *     `node.sector_id` resolves to that exact (site, sectorCode). There is no
 *     farm-scoped fallback: a sector with no assigned pressure sensor shows
 *     "not assigned", never the neighbouring sector's pressure.
 *   - A metric read from a STALE node is not a current metric. It is reported
 *     with `freshness: 'stale'` and its value is not presented as live.
 *   - An ABSENT metric is absent (`—`). It never becomes a fabricated 0.
 *     A real measured 0 stays 0 (`isFiniteNumber` accepts it).
 *   - Irrigation state comes from the sector's valve CONTROLLER only. Pressure
 *     never defines ACTIVE/STOPPED (it may later qualify an anomaly).
 *   - "Depuis quand" requires a PROVEN transition in history. An unproven or
 *     unknown-predecessor run yields null — the UI then says "durée inconnue".
 *
 * Pure helpers only — no React, no network, no side effects.
 */

import type { IoTNode } from './iotApi';
import {
  getAirHumidity,
  getAirTemperature,
  getFlow,
  getPressure,
  getSoilEc,
  getSoilMoisture,
  getSoilTemperature,
  isValveCapable,
  type Measures,
} from './liveSensorMetrics';
import {
  FRESHNESS_MS,
  deriveOperationalState,
  deriveStateSince,
  freshestValveNode,
  nodeTimestamp,
  nodesForSector,
  type OperationalState,
  type SectorPlacement,
  type TelemetryPoint,
} from './operationalOverview';

export type MetricFreshness = 'fresh' | 'stale' | 'absent';

/** The semantic metric keys a sector can present. */
export type SectorMetricKey =
  | 'soilMoisture'
  | 'soilTemperature'
  | 'soilEc'
  | 'pressure'
  | 'flow'
  | 'airTemperature'
  | 'airHumidity';

/**
 * One sector-scoped metric reading.
 *
 * `value === null` means the metric is genuinely absent from every assigned
 * node — the UI must render "—" / "non affecté", NEVER 0.
 */
export interface SectorMetric {
  key: SectorMetricKey;
  value: number | null;
  unitKey: string;
  digits: number;
  sourceNodeId: string | null;
  timestampMs: number | null;
  freshness: MetricFreshness;
}

/** Health of one assigned node, as shown in the equipment list. */
export type EquipmentStatus = 'online' | 'stale' | 'unknown';

export interface SectorEquipment {
  node: IoTNode;
  nodeId: string;
  role: 'controller' | 'soil' | 'pressure' | 'relay' | 'other';
  status: EquipmentStatus;
  timestampMs: number | null;
}

export interface SectorOperationalModel {
  /** Canonical backend sector id, when the placement resolved one. */
  sectorId: string | null;
  siteName: string;
  sectorCode: string;

  controller: IoTNode | undefined;
  soilSensor: IoTNode | undefined;
  pressureSensor: IoTNode | undefined;
  otherAssignedNodes: IoTNode[];
  assignedNodes: IoTNode[];
  equipment: SectorEquipment[];

  operationalState: OperationalState;
  /** Duration in ms the sector has held `operationalState`, or null if unproven. */
  stateSinceMs: number | null;
  /** Epoch ms of the controller's freshest confirmation, or null. */
  lastControllerConfirmationMs: number | null;
  /** True when a controller exists but its telemetry is older than the window. */
  controllerStale: boolean;

  metrics: Record<SectorMetricKey, SectorMetric>;

  onlineCount: number;
  totalCount: number;
}

// ---------------------------------------------------------------------------
// Metric definitions — how each metric is read and displayed.
// ---------------------------------------------------------------------------

interface MetricDef {
  key: SectorMetricKey;
  unitKey: string;
  digits: number;
  getter: (measures: Measures | undefined) => number | undefined;
}

export const SECTOR_METRIC_DEFS: readonly MetricDef[] = [
  { key: 'soilMoisture', unitKey: 'percent', digits: 1, getter: getSoilMoisture },
  { key: 'soilTemperature', unitKey: 'celsius', digits: 1, getter: getSoilTemperature },
  { key: 'soilEc', unitKey: 'ec', digits: 0, getter: getSoilEc },
  { key: 'pressure', unitKey: 'bar', digits: 2, getter: getPressure },
  { key: 'flow', unitKey: 'lpm', digits: 1, getter: getFlow },
  { key: 'airTemperature', unitKey: 'celsius', digits: 1, getter: getAirTemperature },
  { key: 'airHumidity', unitKey: 'percent', digits: 1, getter: getAirHumidity },
];

/**
 * Read one metric from the sector's assigned nodes, keeping the source node and
 * its timestamp so the UI can show provenance and age.
 *
 * The freshest DATED node wins. A node with no usable timestamp only wins when
 * no dated candidate produced the metric, and is reported as `unknown` age
 * (freshness 'stale') because it cannot be proven current.
 */
export function readSectorMetric(
  nodes: IoTNode[],
  def: MetricDef,
  now: number,
): SectorMetric {
  let best: { value: number; nodeId: string; ts: number | null } | undefined;

  for (const node of nodes) {
    const value = def.getter(node.latest?.measures);
    // A real measured 0 is a valid reading; only non-finite values are rejected.
    if (value === undefined || !Number.isFinite(value)) continue;
    const ts = nodeTimestamp(node);
    if (!best) {
      best = { value, nodeId: node.id, ts };
      continue;
    }
    const bestTs = best.ts ?? Number.NEGATIVE_INFINITY;
    const candidateTs = ts ?? Number.NEGATIVE_INFINITY;
    if (candidateTs > bestTs) best = { value, nodeId: node.id, ts };
  }

  if (!best) {
    return {
      key: def.key,
      value: null,
      unitKey: def.unitKey,
      digits: def.digits,
      sourceNodeId: null,
      timestampMs: null,
      freshness: 'absent',
    };
  }

  const fresh = best.ts !== null && now - best.ts < FRESHNESS_MS;
  return {
    key: def.key,
    value: best.value,
    unitKey: def.unitKey,
    digits: def.digits,
    sourceNodeId: best.nodeId,
    timestampMs: best.ts,
    freshness: fresh ? 'fresh' : 'stale',
  };
}

/** Classify one assigned node's communication health. */
export function equipmentStatusOf(node: IoTNode, now: number): EquipmentStatus {
  const ts = nodeTimestamp(node);
  if (ts === null) return 'unknown';
  return now - ts < FRESHNESS_MS ? 'online' : 'stale';
}

/** Business role of an assigned node, derived from its model only. */
export function equipmentRoleOf(node: IoTNode): SectorEquipment['role'] {
  if (isValveCapable(node.model)) return 'controller';
  switch (node.model) {
    case 'soil_air_ecth':
      return 'soil';
    case 'pipe_pressure':
      return 'pressure';
    case 'relay_4ch':
      return 'relay';
    default:
      return 'other';
  }
}

/** The freshest node of a given model among a sector's assigned nodes. */
const freshestOfModel = (nodes: IoTNode[], model: string): IoTNode | undefined => {
  let best: IoTNode | undefined;
  let bestTs = Number.NEGATIVE_INFINITY;
  for (const node of nodes) {
    if (node.model !== model) continue;
    const ts = nodeTimestamp(node) ?? Number.NEGATIVE_INFINITY;
    if (!best || ts > bestTs) {
      best = node;
      bestTs = ts;
    }
  }
  return best;
};

export interface BuildSectorModelInput {
  nodes: IoTNode[];
  placement: Map<string, SectorPlacement>;
  siteName: string;
  sectorCode: string;
  now: number;
  /** Optional controller telemetry history, keyed by node id, for "depuis quand". */
  historyByNodeId?: Map<string, TelemetryPoint[]>;
  commissionedNodes?: IoTNode[];
}

/**
 * Build the complete operational model of one sector.
 *
 * This is the shared truth consumed by BOTH the Dashboard mini-card and the
 * Sector Detail page, so the two can never disagree about a sector's state,
 * metrics, provenance or freshness.
 */
export function buildSectorModel(input: BuildSectorModelInput): SectorOperationalModel {
  const { nodes, placement, siteName, sectorCode, now, historyByNodeId } = input;

  // The ONLY admission rule: canonical node.sector_id resolving to this exact
  // (site, sector). No farm-scoped fallback, ever.
  const assignedNodes = input.commissionedNodes ?? nodesForSector(nodes, placement, siteName, sectorCode);

  const controller = freshestValveNode(assignedNodes);
  const soilSensor = freshestOfModel(assignedNodes, 'soil_air_ecth');
  const pressureSensor = freshestOfModel(assignedNodes, 'pipe_pressure');
  const identified = new Set(
    [controller?.id, soilSensor?.id, pressureSensor?.id].filter(
      (id): id is string => typeof id === 'string',
    ),
  );
  const otherAssignedNodes = assignedNodes.filter((node) => !identified.has(node.id));

  const operationalState = deriveOperationalState(controller, now);
  const lastControllerConfirmationMs = controller ? nodeTimestamp(controller) : null;
  const controllerStale =
    controller !== undefined
    && (lastControllerConfirmationMs === null
      || now - lastControllerConfirmationMs >= FRESHNESS_MS);

  // "Depuis quand" is only derived for a state a transition can prove.
  let stateSinceMs: number | null = null;
  if (controller && (operationalState === 'active' || operationalState === 'stopped')) {
    const history = historyByNodeId?.get(controller.id) ?? [];
    stateSinceMs = deriveStateSince(history, operationalState, now);
  }

  const metrics = {} as Record<SectorMetricKey, SectorMetric>;
  for (const def of SECTOR_METRIC_DEFS) {
    metrics[def.key] = readSectorMetric(assignedNodes, def, now);
  }

  const equipment: SectorEquipment[] = assignedNodes.map((node) => ({
    node,
    nodeId: node.id,
    role: equipmentRoleOf(node),
    status: equipmentStatusOf(node, now),
    timestampMs: nodeTimestamp(node),
  }));

  const onlineCount = equipment.filter((item) => item.status === 'online').length;

  const sectorId =
    assignedNodes.find((node) => typeof node.sector_id === 'string' && node.sector_id.length > 0)
      ?.sector_id ?? null;

  return {
    sectorId,
    siteName,
    sectorCode,
    controller,
    soilSensor,
    pressureSensor,
    otherAssignedNodes,
    assignedNodes,
    equipment,
    operationalState,
    stateSinceMs,
    lastControllerConfirmationMs,
    controllerStale,
    metrics,
    onlineCount,
    totalCount: assignedNodes.length,
  };
}

// ---------------------------------------------------------------------------
// Canonical sector → valve-channel binding (section 13).
// ---------------------------------------------------------------------------

/**
 * The outcome of resolving which physical valve channel(s) a sector command
 * would actuate.
 *
 *  - `bound`   — the backend canonically proved (node, channels) for this
 *                sector; a sector command may target exactly those channels.
 *  - `unbound` — no canonical binding exists. The sector controls MUST stay
 *                disabled and issue ZERO POST.
 */
export type SectorValveBinding =
  | { kind: 'bound'; nodeId: string; channelIds: string[] }
  | { kind: 'unbound'; reason: 'no_controller' | 'no_canonical_binding' };

/**
 * Resolve the canonical sector → valve-channel binding.
 *
 * AUDIT RESULT (R2, backend @ 2115bae): **no such binding exists**.
 * `node.sector_id` binds a whole CONTROLLER NODE to a sector (via
 * `pack.sector_id`), but an ASVWF01 controller drives valve_1..valve_4 and
 * NOTHING in the backend states which channel serves which sector or parcel.
 * The commissioning parcel/channel bindings are still PLANNED, not physically
 * confirmed.
 *
 * Therefore this resolver deliberately returns `unbound` whenever the runtime
 * data does not PROVE the channel mapping. We do NOT assume "A = valve_1", and
 * we never fall back to commanding all four channels — either of those would
 * open the wrong parcel in the field.
 *
 * When the backend later exposes a canonical binding (e.g. a per-channel
 * `sector_id`, or a `sector_channel_bindings` document), THIS FUNCTION is the
 * only place that must change: every caller already fails closed on `unbound`.
 */
export function resolveSectorValveBinding(
  model: Pick<SectorOperationalModel, 'controller' | 'sectorId'>,
): SectorValveBinding {
  const controller = model.controller;
  if (!controller) return { kind: 'unbound', reason: 'no_controller' };

  // A legacy single-valve controller has exactly one physical output, so a
  // node-level sector binding IS the channel binding — no ambiguity possible.
  if (controller.model === 'valve_water_flow') {
    return { kind: 'bound', nodeId: controller.id, channelIds: [] };
  }

  // Multi-channel controller (ASVWF01): the node is sector-bound but the
  // per-channel mapping is not canonically known. Fail closed.
  return { kind: 'unbound', reason: 'no_canonical_binding' };
}
